/**
 * One winner's date and note fields (ISSUE-1291, ISSUE-1293), shared by the
 * board's winner sheet and the Results edit.
 */
import { Button, cn, FOCUS_RING, Input } from "@sportsmet/ui";
import { type ReactNode, useId } from "react";

import {
  STRIP_WINNER_DATE_LABEL,
  STRIP_WINNER_NOTE_HINT,
  STRIP_WINNER_NOTE_LABEL,
  STRIP_WINNER_REMOVE_LABEL,
  stripWinnerItemHeading,
} from "./stripCardCopy";
import {
  STRIP_WINNER_NOTE_MAX_LENGTH,
  type typeStripWinnerDraft,
} from "./stripWinnerDraft";

export const StripWinnerFields = (props: {
  index: number;
  draft: typeStripWinnerDraft;
  error: { day?: string; note?: string } | undefined;
  onChange: (change: Partial<typeStripWinnerDraft>) => void;
  /** Overrides "Winner N". */
  heading?: string;
  /** Omit to hide Remove, as the Results edit does. */
  onRemove?: () => void;
}): ReactNode => {
  const noteId = useId();
  const noteErrorId = `${noteId}-error`;
  const noteHintId = `${noteId}-hint`;
  const noteError = props.error?.note;
  const headingId = `${noteId}-heading`;
  return (
    <div
      role="group"
      aria-labelledby={headingId}
      data-testid={`strip-winner-${String(props.index)}`}
      className="flex flex-col gap-3 rounded-md border border-line p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <span id={headingId} className="text-body-strong text-ink">
          {props.heading ?? stripWinnerItemHeading(props.index)}
        </span>
        {props.onRemove !== undefined ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid={`strip-winner-remove-${String(props.index)}`}
            onClick={props.onRemove}
          >
            {STRIP_WINNER_REMOVE_LABEL}
          </Button>
        ) : null}
      </div>
      <Input
        type="date"
        label={STRIP_WINNER_DATE_LABEL}
        value={props.draft.day}
        error={props.error?.day}
        data-testid={`strip-winner-date-${String(props.index)}`}
        onChange={(event) => {
          props.onChange({ day: event.target.value });
        }}
      />
      <div className="flex flex-col gap-1">
        <label htmlFor={noteId} className="text-caption text-ink">
          {STRIP_WINNER_NOTE_LABEL}
        </label>
        <textarea
          id={noteId}
          rows={3}
          maxLength={STRIP_WINNER_NOTE_MAX_LENGTH}
          value={props.draft.note}
          data-testid={`strip-winner-note-${String(props.index)}`}
          aria-invalid={noteError !== undefined || undefined}
          aria-describedby={noteError !== undefined ? noteErrorId : noteHintId}
          onChange={(event) => {
            props.onChange({ note: event.target.value });
          }}
          className={cn(
            "min-h-touch-target w-full rounded-sm border bg-surface px-3 py-3 text-input text-ink",
            noteError !== undefined ? "border-error" : "border-line",
            FOCUS_RING,
          )}
        />
        {noteError !== undefined ? (
          <p id={noteErrorId} role="alert" className="text-caption text-error">
            {noteError}
          </p>
        ) : (
          <p id={noteHintId} className="text-caption text-ink-secondary">
            {STRIP_WINNER_NOTE_HINT}
          </p>
        )}
      </div>
    </div>
  );
};
